/**
 * The mock AI summary. It is written from the same builders the Overview's
 * cards use, and formats numbers with the same functions, so every figure it
 * quotes is one the viewer can find on the page. The real digest arrives
 * behind a `Generator` port (pivot doc §7, assumption 4); this stands in for it.
 */

import type { Alert, Digest, KpiKey, PacingStatus } from '@/api/types'
import { formatCurrency, formatInteger, formatPercent, formatRatio, percentChange } from '@/design/format'
import type { GlobalFilters } from '@/lib/filters'

import { buildAlerts } from './alerts'
import type { DemoDataset } from './engine'
import {
  buildChannels,
  buildKpis,
  buildTopCompetitions,
  type Built,
  type DemoChannelPerformance,
  type DemoCompetitionPerformance,
  type DemoKpi,
} from './overview'

const PACING_PHRASES: Record<PacingStatus, string> = {
  ahead: 'ahead of pace',
  on_track: 'on pace',
  behind: 'behind pace',
}

function periodPhrase(label: string): string {
  if (label === 'Today' || label === 'Yesterday') return label
  if (label === 'Month to date') return 'So far this month'
  if (label === 'Previous month') return 'Last month'
  if (label.startsWith('Last ')) return `Over the ${label.toLowerCase()}`
  return `Between ${label}`
}

export interface DigestInputs {
  tenantName: string
  currency: string
  locale: string
  periodLabel: string
  compareLabel: string | null
  kpis: DemoKpi[]
  competitions: DemoCompetitionPerformance[]
  channels: DemoChannelPerformance[]
  alerts: Alert[]
}

export function writeDigest(inputs: DigestInputs): Pick<Digest, 'headline' | 'paragraph'> {
  const money = { currency: inputs.currency, locale: inputs.locale }
  const kpi = (key: KpiKey) => inputs.kpis.find((item) => item.key === key)
  const revenue = kpi('revenue')?.value ?? 0
  const change = percentChange(revenue, kpi('revenue')?.previous)
  const direction = change !== null && change < 0 ? 'down' : 'up'
  const changeClause =
    change === null || !inputs.compareLabel
      ? ''
      : `, ${direction} ${formatPercent(Math.abs(change))} ${inputs.compareLabel}`

  const sentences = [
    `${periodPhrase(inputs.periodLabel)}, ${inputs.tenantName} took ${formatCurrency(revenue, money)} from ${formatInteger(kpi('orders')?.value ?? 0)} orders and ${formatInteger(kpi('tickets')?.value ?? 0)} entries${changeClause}.`,
    `Conversion ran at ${formatPercent(kpi('conversion_rate')?.value ?? 0)} with an average order of ${formatCurrency(kpi('aov')?.value ?? 0, money)}.`,
  ]

  const top = inputs.competitions[0]
  if (top) {
    sentences.push(
      `${top.entity.label} led competitions with ${formatCurrency(top.revenue, money)} and is ${PACING_PHRASES[top.pacing]} at ${formatPercent(top.sellThrough, 0)} sold.`,
    )
  }

  // "Strongest ROAS" is judged among channels with real budget behind them;
  // a near-free email send would otherwise always win.
  const totalSpend = inputs.channels.reduce((sum, channel) => sum + channel.spend, 0)
  const paid = inputs.channels.filter(
    (channel) => channel.roas !== null && channel.spend > 0 && channel.spend >= totalSpend * 0.05,
  )
  const best = [...paid].sort((a, b) => (b.roas ?? 0) - (a.roas ?? 0))[0]
  const offline = inputs.channels
    .filter((channel) => channel.kind === 'offline')
    .sort((a, b) => b.attributedRevenue - a.attributedRevenue)[0]
  if (best) {
    sentences.push(
      `${best.entity.label} returned the strongest ROAS at ${formatRatio(best.roas ?? 0)} on ${formatCurrency(best.spend, money)} spend${
        offline
          ? `, and ${offline.entity.label} led offline channels with ${formatCurrency(offline.attributedRevenue, money)} attributed revenue`
          : ''
      }.`,
    )
  }

  const [first, ...rest] = inputs.alerts
  sentences.push(
    first
      ? `Needs attention: ${first.title}${rest.length ? `, plus ${rest.length} more active ${rest.length === 1 ? 'alert' : 'alerts'}` : ''}.`
      : 'No active alerts need attention.',
  )

  const headline =
    change === null || !inputs.compareLabel
      ? `${formatCurrency(revenue, money)} revenue ${inputs.periodLabel.toLowerCase()}`
      : `Revenue ${direction} ${formatPercent(Math.abs(change))} ${inputs.compareLabel}`

  return { headline, paragraph: sentences.join(' ') }
}

export function buildDigest(dataset: DemoDataset, filters: GlobalFilters): Built<Digest> {
  const kpis = buildKpis(dataset, filters)
  const written = writeDigest({
    tenantName: dataset.tenant.name,
    currency: dataset.tenant.currency,
    locale: dataset.tenant.locale,
    periodLabel: kpis.period?.label ?? '',
    compareLabel: kpis.period?.compareLabel ?? null,
    kpis: kpis.data,
    competitions: buildTopCompetitions(dataset, filters, 1).data,
    channels: buildChannels(dataset, filters).data,
    alerts: buildAlerts(dataset, filters),
  })
  return {
    data: {
      ...written,
      generatedAt: new Date(dataset.now.getTime() - 4 * 60_000).toISOString(),
      drillDown: { path: '/insights/digest' },
    },
    period: kpis.period,
  }
}
